import { Component, Input, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { Camera, CameraResultType } from "@capacitor/camera";
import { Device } from "@capacitor/device";
import { ModalController, Platform } from "@ionic/angular";
import { finalize } from "rxjs/operators";
import { AuthApiService } from "src/app/core/api/auth-api.service";
import { EmployeeApiService } from "src/app/core/api/employee-api.service";
import { EmployeeField } from "src/app/core/models/employee.model";

import { ServiceDeskApiService } from "../../../core/api/service-desk-api.service";
import { IFeedback } from "../../../core/models/service-desk.model";
import { EnvironmentService } from "../../../core/services/environment.service";
import { ToastService } from "../../../core/services/toast.service";
import { OwnerApiService } from "src/app/core/api/owner-api.service";
import { OrganizationApiService } from "src/app/core/api/organization-api.service";
import { OrganizationField } from "src/app/core/models/organization.model";
import { OwnerField } from "src/app/core/models/owner.model";

@Component({
	selector: "af-feedback-modal",
	templateUrl: "./feedback-modal.component.html"
})
export class FeedbackModalComponent implements OnInit {
	form: FormGroup;
	isSubmitting = false;
	activeEmployee: string;
	activeEmployeeId: string;
	organizationName: string;
	ownerName: string;
	ownerId: string;
	organizationId: string;
	deviceInfo: any;
	currentRoute: string;
	activeModules = "";
	@Input() screenshot: string;

	constructor(
		private environmentService: EnvironmentService,
		private modalController: ModalController,
		private serviceDeskApiService: ServiceDeskApiService,
		private toastService: ToastService,
		private employeeApiService: EmployeeApiService,
		private ownerApiService: OwnerApiService,
		private organizationApiService: OrganizationApiService,
		private platform: Platform,
		private router: Router,
		private authApiService: AuthApiService
	) {}

	async ngOnInit() {
		this.form = new FormGroup({
			type: new FormControl("question", Validators.required),
			subject: new FormControl("", Validators.required),
			body: new FormControl("", Validators.required)
		});
		await this.getActiveEmployee();
		await this.getOrganization();
		await this.getOwner();
		this.getActiveModules();
		this.deviceInfo = await Device.getInfo();

		this.currentRoute = this.router.url;
	}

	dismiss() {
		this.modalController.dismiss();
	}

	async addScreenshot() {
		try {
			const photo = await Camera.getPhoto({
				quality: 92,
				resultType: CameraResultType.DataUrl
			}).catch((error) => {
				console.log(error.message);
			});
			if (photo) {
				this.screenshot = photo.dataUrl;
			} else {
				this.screenshot = null;
				console.log("Geen screenshot beschkikbaar");
			}
		} catch (error) {}
	}

	removeScreenshot() {
		this.screenshot = null;
	}

	async getActiveEmployee() {
		await new Promise<void>((resolve) => {
			this.employeeApiService.getActiveEmployee([EmployeeField.Fullname, EmployeeField.OrganizationId]).subscribe((res) => {
				this.activeEmployee = res.data[0].v_display_name;
				this.activeEmployeeId = res.data[0].employee_id;
				this.organizationId = res.data[0].organization_id;
				resolve();
			});
		});
	}

	async getOrganization() {
		// promse resolve
		await new Promise<void>((resolve) => {
			this.organizationApiService
				.getOrganization(this.organizationId, [OrganizationField.Name, OrganizationField.OrganizationId, OrganizationField.OwnerId])
				.subscribe((res) => {
					this.organizationName = res.data[0].name;
					this.ownerId = res.data[0].owner_id;
					resolve();
				});
		});
	}

	async getOwner() {
		// promse resolve
		await new Promise<void>((resolve) => {
			this.ownerApiService.getOwner(this.ownerId, [OwnerField.Name, OwnerField.OwnerId]).subscribe((ownerRes) => {
				this.ownerName = ownerRes.data[0].name;
				resolve();
			});
		});
	}

	getActiveModules() {
		let seperator = "";
		this.authApiService.getModules().subscribe((modules) => {
			modules.modules.data.forEach((module) => {
				this.activeModules += seperator + module.name;
				seperator = ", ";
			});
		});
	}

	submit() {
		if (this.form.invalid) return;

		this.isSubmitting = true;

		const activeFormText = "Active form";
		const modulesText = "Modules";
		const platformsText = "Platforms";

		const feedback: IFeedback = {
			body:
				this.form.value.body +
				`\n----\n{code:title=${activeFormText}|language=plaintext}` +
				this.currentRoute +
				`{code}\n{code:title=Employee (Personeelslid): ${this.activeEmployee}|language=plaintext}` +
				this.activeEmployeeId +
				`{code}\n{code:title=Customer (Organisatie): ${this.ownerName}|language=plaintext}` +
				this.ownerId +
				`{code}\n{code:title=Organisatie (Vestiging): ${this.organizationName}|language=plaintext}` +
				this.organizationId +
				`{code}\n{code:title=${modulesText}|language=json}` +
				JSON.stringify(this.activeModules) +
				`{code}\n{code:title=${platformsText}|language=plaintext}` +
				this.platform.platforms().join(", ") +
				" | Device info: " +
				"Model: " +
				this.deviceInfo.model +
				" | Operating system: " +
				this.deviceInfo.operatingSystem +
				" | OS Version: " +
				this.deviceInfo.osVersion +
				`{code}`,
			environment: this.environmentService.environment,
			screenshot: this.screenshot ? this.screenshot.split(",")[1] : "",
			subject: "App: " + this.form.value.subject,
			type: this.form.value.type,
			version: this.environmentService.version
		};

		this.serviceDeskApiService
			.addFeedback(feedback)
			.pipe(finalize(() => (this.isSubmitting = false)))
			.subscribe({
				next: () => {
					this.modalController.dismiss();
					this.toastService.presentSuccessToast("Feedback verzonden");
				},
				error: (error) => {
					this.toastService.presentFailureToast(error.error.errorMessage);
				}
			});
	}
}
